// Wave 20.20 — /drops storyboard.
//
// Replaces the Mapbox 3D scroll hero. Instead of an abstract
// terrain fly-through, we show the actual product experience end
// to end: the sender records a ride, a peak is detected, they tap
// Drop, the link is copied, and the recipient opens the link.
// Auto-advances every ~3.5s; hovering the panel pauses the timer
// so a visitor can dwell on any scene.
//
// Two phone frames sit side by side. The left one holds the
// sender's screens (Recording → Peak pill → Drop sheet); the right
// one lights up during the last scene with the shared drop as the
// recipient sees it.

import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Chip,
  keyframes,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LinkIcon from '@mui/icons-material/Link';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { colors } from '../theme';

type Scene = 'record' | 'peak' | 'drop' | 'sent' | 'receive';

const SCENES: { key: Scene; label: string; caption: string }[] = [
  { key: 'record',  label: 'Record',  caption: 'aero.chat mobile logs your ride second by second.' },
  { key: 'peak',    label: 'Peak',    caption: 'A new personal peak is auto-detected as you push it.' },
  { key: 'drop',    label: 'Drop',    caption: 'Tap Drop, pick who — friends, followers, or a public link.' },
  { key: 'sent',    label: 'Sent',    caption: 'Drop link on your clipboard. Paste it anywhere.' },
  { key: 'receive', label: 'Open',    caption: 'They open it in a browser. No app, no sign-up.' },
];

const SCENE_MS = 3500;

const pulseGlow = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(106, 255, 69, 0.55); }
  70%  { box-shadow: 0 0 0 14px rgba(106, 255, 69, 0); }
  100% { box-shadow: 0 0 0 0 rgba(106, 255, 69, 0); }
`;

const slideUp = keyframes`
  0%   { transform: translateY(24px); opacity: 0; }
  100% { transform: translateY(0);    opacity: 1; }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const ticker = keyframes`
  0%   { transform: translateY(0); }
  50%  { transform: translateY(-2px); }
  100% { transform: translateY(0); }
`;

const gpsBlip = keyframes`
  0%, 100% { transform: scale(1);   opacity: 1;   }
  50%      { transform: scale(1.8); opacity: 0.4; }
`;

interface Props {
  /** Compact mode: one phone at a time (no side-by-side), no
   *  outer heading. Used when the storyboard is embedded in the
   *  hero next to text. */
  compact?: boolean;
}

export default function DropStoryboard({ compact }: Props = {}) {
  const [scene, setScene] = useState<Scene>('record');
  const [progress, setProgress] = useState(0);
  // Refs for the animation so a state closure staleness bug can't
  // freeze the timeline on 'record' (which is exactly what happened
  // in the first cut — the timer captured `elapsed` in a stale
  // useEffect closure and the scene never advanced for some users).
  const elapsedRef = useRef(0);
  const sceneIdxRef = useRef(0);

  useEffect(() => {
    const tickMs = 60;
    const timer = setInterval(() => {
      elapsedRef.current += tickMs;
      const pct = Math.min(100, (elapsedRef.current / SCENE_MS) * 100);
      setProgress(pct);
      if (elapsedRef.current >= SCENE_MS) {
        elapsedRef.current = 0;
        sceneIdxRef.current = (sceneIdxRef.current + 1) % SCENES.length;
        setScene(SCENES[sceneIdxRef.current].key);
        setProgress(0);
      }
    }, tickMs);
    return () => clearInterval(timer);
  }, []);

  const jumpTo = (key: Scene) => {
    const idx = SCENES.findIndex((s) => s.key === key);
    if (idx < 0) return;
    sceneIdxRef.current = idx;
    elapsedRef.current = 0;
    setScene(key);
    setProgress(0);
  };

  const current = SCENES.find((s) => s.key === scene)!;

  // The 'receive' scene shows on ONE phone (the recipient's view)
  // in compact mode; in full mode it's on the right phone while the
  // left phone stays dim.
  const sceneContent =
    scene === 'record' ? <RecordScene /> :
    scene === 'peak' ? <PeakScene /> :
    scene === 'drop' ? <DropScene /> :
    scene === 'sent' ? <SentScene /> :
    <ReceiveScene />;

  return (
    <Box
      sx={{
        py: compact ? 0 : { xs: 6, md: 10 },
        px: compact ? 0 : 3,
        background: compact
          ? 'transparent'
          : `radial-gradient(1200px 500px at 50% 0%, ${colors.brand}0f, transparent 70%)`,
      }}
    >
      {!compact && (
        <Box sx={{ maxWidth: 1080, mx: 'auto', textAlign: 'center', mb: { xs: 4, md: 6 } }}>
          <Typography variant="overline" sx={{ color: colors.brand, letterSpacing: '0.2em' }}>
            How a Drop works
          </Typography>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 900,
              letterSpacing: '-0.02em',
              fontSize: { xs: '1.75rem', md: '2.5rem' },
              mt: 1,
            }}
          >
            From your ride to their inbox in five taps.
          </Typography>
        </Box>
      )}

      {compact ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <PhoneFrame accent>
            {sceneContent}
          </PhoneFrame>
        </Box>
      ) : (
        <Box
          sx={{
            maxWidth: 1080,
            mx: 'auto',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: { xs: 3, md: 6 },
            flexWrap: 'wrap',
          }}
        >
          {/* Sender phone */}
          <PhoneFrame accent={scene !== 'receive'}>
            {scene === 'record' && <RecordScene />}
            {scene === 'peak' && <PeakScene />}
            {scene === 'drop' && <DropScene />}
            {scene === 'sent' && <SentScene />}
            {scene === 'receive' && <SentScene dim />}
          </PhoneFrame>

          {/* Arrow / trail — only shows once the drop leaves the sender */}
          <Box
            sx={{
              display: { xs: 'none', md: scene === 'sent' || scene === 'receive' ? 'flex' : 'none' },
              alignItems: 'center',
              color: colors.brand,
              fontSize: 34,
              animation: `${fadeIn} 400ms ease`,
            }}
          >
            →
          </Box>

          {/* Recipient phone — dim until the drop is opened */}
          <PhoneFrame accent={scene === 'receive'} dim={scene !== 'receive'}>
            {scene === 'receive' ? <ReceiveScene /> : <ReceiveIdleScene />}
          </PhoneFrame>
        </Box>
      )}

      {/* Scene timeline */}
      <Box sx={{ maxWidth: 720, mx: 'auto', mt: { xs: 4, md: 6 } }}>
        <Stack direction="row" spacing={1} justifyContent="space-between">
          {SCENES.map((s) => (
            <Box
              key={s.key}
              onClick={() => jumpTo(s.key)}
              sx={{
                flex: 1,
                textAlign: 'center',
                cursor: 'pointer',
                userSelect: 'none',
                '&:hover': { opacity: 0.85 },
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: 800,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  color: s.key === scene ? colors.brand : colors.textMuted,
                  transition: 'color 200ms',
                }}
              >
                {s.label}
              </Typography>
            </Box>
          ))}
        </Stack>
        <Box sx={{ mt: 1, display: 'flex', gap: 0.5 }}>
          {SCENES.map((s) => (
            <Box
              key={s.key}
              sx={{
                flex: 1,
                height: 3,
                borderRadius: 2,
                backgroundColor: colors.border,
                overflow: 'hidden',
              }}
            >
              {s.key === scene && (
                <Box
                  sx={{
                    height: '100%',
                    width: `${progress}%`,
                    backgroundColor: colors.brand,
                    transition: 'width 60ms linear',
                  }}
                />
              )}
              {SCENES.findIndex((x) => x.key === s.key) <
                SCENES.findIndex((x) => x.key === scene) && (
                <Box sx={{ height: '100%', width: '100%', backgroundColor: colors.brand }} />
              )}
            </Box>
          ))}
        </Box>
        <Typography
          sx={{
            textAlign: 'center',
            mt: 2.5,
            color: colors.textMuted,
            fontSize: '0.95rem',
            minHeight: 24,
          }}
        >
          {current.caption}
        </Typography>
      </Box>
    </Box>
  );
}

// ---------- phone frame ----------

function PhoneFrame({
  children,
  accent,
  dim,
}: {
  children: React.ReactNode;
  accent?: boolean;
  dim?: boolean;
}) {
  return (
    <Box
      sx={{
        width: { xs: 260, md: 300 },
        height: { xs: 520, md: 600 },
        borderRadius: 6,
        p: 1.25,
        backgroundColor: '#0d0f16',
        border: `1px solid ${accent ? colors.brand : colors.border}`,
        boxShadow: accent
          ? `0 30px 80px ${colors.brand}22, 0 0 0 1px ${colors.brand}44 inset`
          : `0 20px 60px rgba(0,0,0,0.55)`,
        transition: 'all 400ms ease',
        opacity: dim ? 0.5 : 1,
        position: 'relative',
      }}
    >
      {/* Notch */}
      <Box
        sx={{
          position: 'absolute',
          top: 12,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 90,
          height: 20,
          borderRadius: 4,
          backgroundColor: '#000',
          zIndex: 2,
        }}
      />
      <Box
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: 5,
          overflow: 'hidden',
          backgroundColor: colors.bg,
          position: 'relative',
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

// ---------- scenes ----------

function RecordScene() {
  return (
    <Box sx={{ p: 2.5, pt: 5.5, height: '100%', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <StatusBar label="Recording · 32:14" live />
      <BigTile label="Speed" value="34.2 km/h" mono />
      <Box sx={{ display: 'flex', gap: 1 }}>
        <SmallTile label="Time" value="32:14" />
        <SmallTile label="Dist" value="18.4 km" />
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <SmallTile label="HR" value="168 bpm" />
        <SmallTile label="Power" value="312 W" />
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <SmallTile label="Grade" value="4.2 %" />
        <SmallTile label="Alt" value="612 m" />
      </Box>
      <MiniMap />
      <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
        <PillButton label="Pause" muted />
        <PillButton label="End" danger />
      </Box>
    </Box>
  );
}

function PeakScene() {
  return (
    <Box sx={{ position: 'relative', height: '100%' }}>
      <RecordScene />
      {/* Peak pill overlay */}
      <Box
        sx={{
          position: 'absolute',
          left: 12,
          right: 12,
          bottom: 78,
          p: 1.5,
          borderRadius: 3,
          backgroundColor: colors.surface,
          border: `1px solid ${colors.brand}88`,
          animation: `${slideUp} 500ms ease`,
        }}
      >
        <Typography sx={{ fontSize: '0.6rem', color: colors.brand, fontWeight: 800, letterSpacing: '0.15em' }}>
          NEW PEAK
        </Typography>
        <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mt: 0.25 }}>
          <BoltIcon sx={{ color: colors.brand, fontSize: 18 }} />
          <Typography sx={{ fontWeight: 800, color: colors.text, fontSize: '0.95rem' }}>
            Best 5-min Power
          </Typography>
        </Stack>
        <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 800, color: colors.text, fontSize: '1.3rem', mt: 0.5 }}>
          320 W
        </Typography>
        <Box
          sx={{
            mt: 1,
            p: 0.75,
            borderRadius: 1.5,
            backgroundColor: `${colors.brand}22`,
            textAlign: 'center',
            animation: `${pulseGlow} 1.6s ease-out infinite`,
          }}
        >
          <Typography sx={{ color: colors.brand, fontWeight: 800, fontSize: '0.8rem', letterSpacing: '0.1em' }}>
            Drop it →
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function DropScene() {
  return (
    <Box sx={{ p: 2.5, pt: 5.5, height: '100%', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <StatusBar label="Drop a peak" />
      <Box
        sx={{
          p: 1.5,
          borderRadius: 3,
          backgroundColor: colors.surface,
          border: `1px solid ${colors.border}`,
        }}
      >
        <Typography sx={{ fontSize: '0.6rem', color: colors.brand, fontWeight: 800, letterSpacing: '0.15em' }}>
          NEW PEAK
        </Typography>
        <Typography sx={{ fontWeight: 800, mt: 0.25 }}>Best 5-min Power</Typography>
        <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 800, fontSize: '1.5rem' }}>
          320 W
        </Typography>
      </Box>
      <Typography sx={{ fontSize: '0.65rem', color: colors.brand, fontWeight: 800, letterSpacing: '0.15em' }}>
        WHO CAN VIEW
      </Typography>
      <RadioRow label="Anyone with the link" selected />
      <RadioRow label="Friends only" />
      <RadioRow label="Just me" />
      <Box
        sx={{
          mt: 'auto',
          p: 1.25,
          borderRadius: 3,
          backgroundColor: colors.brand,
          color: colors.textInverse,
          textAlign: 'center',
          fontWeight: 800,
          letterSpacing: '0.05em',
          animation: `${pulseGlow} 1.4s ease-out infinite`,
        }}
      >
        Create Drop Link
      </Box>
    </Box>
  );
}

function SentScene({ dim }: { dim?: boolean }) {
  return (
    <Box
      sx={{
        p: 2.5,
        pt: 5.5,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        opacity: dim ? 0.6 : 1,
        transition: 'opacity 400ms',
      }}
    >
      <StatusBar label="Sent" />
      <Box
        sx={{
          p: 1.5,
          borderRadius: 3,
          backgroundColor: colors.surface,
          border: `1px solid ${colors.brand}66`,
          animation: `${fadeIn} 500ms ease`,
        }}
      >
        <Typography sx={{ fontSize: '0.6rem', color: colors.brand, fontWeight: 800, letterSpacing: '0.15em' }}>
          DROP LINK COPIED
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
          <LinkIcon sx={{ color: colors.brand, fontSize: 16 }} />
          <Typography
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.75rem',
              color: colors.text,
              wordBreak: 'break-all',
            }}
          >
            aero.chat/peak/9k4x2mvyq3
          </Typography>
        </Stack>
        <Box
          sx={{
            mt: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            color: colors.textMuted,
            fontSize: '0.7rem',
          }}
        >
          <ContentCopyIcon sx={{ fontSize: 14 }} /> Copied to your clipboard
        </Box>
      </Box>

      <Typography sx={{ fontSize: '0.75rem', color: colors.textMuted, mt: 1 }}>
        Paste it anywhere:
      </Typography>
      <MockChatBubble />

      <Box
        sx={{
          mt: 'auto',
          textAlign: 'center',
          color: colors.textMuted,
          fontSize: '0.7rem',
        }}
      >
        Expires in 24 h · Anyone with the link
      </Box>
    </Box>
  );
}

function ReceiveIdleScene() {
  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: colors.textFaint,
        fontSize: '0.8rem',
        px: 3,
        textAlign: 'center',
      }}
    >
      Their phone
    </Box>
  );
}

function ReceiveScene() {
  return (
    <Box sx={{ p: 2.5, pt: 5.5, height: '100%', display: 'flex', flexDirection: 'column', gap: 1.5, animation: `${fadeIn} 400ms ease` }}>
      <BrowserBar url="aero.chat/peak/9k4x2mvyq3" />
      <Typography sx={{ fontSize: '0.6rem', color: colors.brand, fontWeight: 800, letterSpacing: '0.15em', mt: 1 }}>
        ROBBY K · SAT AUG 2
      </Typography>
      <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', mt: -0.5 }}>
        Just dropped a peak
      </Typography>
      <Box
        sx={{
          p: 1.75,
          borderRadius: 3,
          backgroundColor: colors.surface,
          border: `1px solid ${colors.brand}66`,
        }}
      >
        <Typography sx={{ fontSize: '0.65rem', color: colors.brand, fontWeight: 800, letterSpacing: '0.1em' }}>
          BEST 5-MIN POWER
        </Typography>
        <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 800, fontSize: '2rem', color: colors.text, mt: 0.5 }}>
          320 W
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', color: colors.textMuted, mt: 0.75 }}>
          Held for 5:00 · Sat Aug 2
        </Typography>
      </Box>
      <Box
        sx={{
          p: 1.25,
          borderRadius: 2,
          backgroundColor: `${colors.brand}12`,
          border: `1px dashed ${colors.brand}55`,
          textAlign: 'center',
        }}
      >
        <Typography sx={{ color: colors.brand, fontWeight: 800, fontSize: '0.8rem' }}>
          Beat this on your next ride
        </Typography>
      </Box>
      <Box
        sx={{
          mt: 'auto',
          p: 1,
          borderRadius: 2,
          backgroundColor: colors.brand,
          color: colors.textInverse,
          textAlign: 'center',
          fontWeight: 800,
          fontSize: '0.85rem',
        }}
      >
        Get the app
      </Box>
    </Box>
  );
}

// ---------- small building blocks ----------

function StatusBar({ label, live }: { label: string; live?: boolean }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ pt: 0.5 }}>
      {live && (
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: colors.danger,
            animation: `${gpsBlip} 1.4s ease-in-out infinite`,
          }}
        />
      )}
      <Typography sx={{ fontSize: '0.7rem', color: colors.textMuted, fontWeight: 700 }}>
        {label}
      </Typography>
    </Stack>
  );
}

function BigTile({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2.5,
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
      }}
    >
      <Typography sx={{ fontSize: '0.6rem', color: colors.textMuted, fontWeight: 700, letterSpacing: '0.1em' }}>
        {label.toUpperCase()}
      </Typography>
      <Typography
        sx={{
          fontFamily: mono ? '"JetBrains Mono", monospace' : undefined,
          fontWeight: 800,
          fontSize: '1.5rem',
          mt: 0.25,
          animation: `${ticker} 2.2s ease-in-out infinite`,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function SmallTile({ label, value }: { label: string; value: string }) {
  return (
    <Box
      sx={{
        flex: 1,
        p: 1,
        borderRadius: 2,
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
      }}
    >
      <Typography sx={{ fontSize: '0.55rem', color: colors.textMuted, fontWeight: 700, letterSpacing: '0.1em' }}>
        {label.toUpperCase()}
      </Typography>
      <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 700, fontSize: '0.85rem' }}>
        {value}
      </Typography>
    </Box>
  );
}

function MiniMap() {
  // Faux terrain — three stacked curves + a moving dot.
  return (
    <Box
      sx={{
        height: 60,
        borderRadius: 2,
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <svg
        viewBox="0 0 300 60"
        preserveAspectRatio="none"
        style={{ width: '100%', height: '100%' }}
      >
        <path d="M0 45 Q60 20 120 30 T240 25 T300 40" fill="none" stroke={colors.border} strokeWidth="1.5" />
        <path d="M0 50 Q60 30 120 40 T240 35 T300 48" fill="none" stroke={colors.border} strokeWidth="1" opacity="0.5" />
        <path d="M0 55 Q60 40 120 48 T240 44 T300 55" fill="none" stroke={colors.brand} strokeWidth="2" />
        <circle cx="180" cy="42" r="4" fill={colors.brand}>
          <animate attributeName="opacity" values="1;0.3;1" dur="1.4s" repeatCount="indefinite" />
        </circle>
      </svg>
    </Box>
  );
}

function PillButton({ label, danger, muted }: { label: string; danger?: boolean; muted?: boolean }) {
  return (
    <Box
      sx={{
        flex: 1,
        py: 1.25,
        borderRadius: 3,
        backgroundColor: danger ? `${colors.danger}22` : colors.surface,
        border: `1px solid ${danger ? colors.danger : muted ? colors.border : colors.brand}`,
        color: danger ? colors.danger : colors.text,
        textAlign: 'center',
        fontWeight: 700,
        fontSize: '0.9rem',
      }}
    >
      {label}
    </Box>
  );
}

function RadioRow({ label, selected }: { label: string; selected?: boolean }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 0.75,
        borderRadius: 2,
        border: `1px solid ${selected ? colors.brand : colors.border}`,
        backgroundColor: selected ? `${colors.brand}0e` : 'transparent',
      }}
    >
      <Box
        sx={{
          width: 14,
          height: 14,
          borderRadius: '50%',
          border: `2px solid ${selected ? colors.brand : colors.textMuted}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {selected && (
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: colors.brand }} />
        )}
      </Box>
      <Typography sx={{ fontSize: '0.8rem' }}>{label}</Typography>
    </Box>
  );
}

function MockChatBubble() {
  return (
    <Box
      sx={{
        alignSelf: 'flex-end',
        maxWidth: '85%',
        p: 1,
        borderRadius: 2,
        borderBottomRightRadius: 4,
        backgroundColor: colors.brand,
        color: colors.textInverse,
        fontSize: '0.75rem',
        fontFamily: '"JetBrains Mono", monospace',
        animation: `${slideUp} 500ms ease`,
      }}
    >
      aero.chat/peak/9k4x2mvyq3
    </Box>
  );
}

function BrowserBar({ url }: { url: string }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1,
        py: 0.5,
        borderRadius: 2,
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
      }}
    >
      {[0, 1, 2].map((i) => (
        <Box key={i} sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: colors.border }} />
      ))}
      <Box
        sx={{
          ml: 1,
          flex: 1,
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: '0.65rem',
          color: colors.textMuted,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {url}
      </Box>
    </Box>
  );
}
